/**
 * Logger centralizzato per MaestroERP
 *
 * Uso:
 *   import { logger } from '../core/services/logger'
 *   logger.info('Messaggio')
 *   logger.debug('Debug info', { data })
 *   logger.warn('Attenzione')
 *   logger.error('Errore', error)
 *   logger.api('GET /endpoint', response)
 *
 * Configurazione:
 *   - In sviluppo: tutti i log attivi
 *   - In produzione: solo warn e error (configurabile)
 *   - localStorage.setItem('debug', 'true') per attivare debug in produzione
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'api'

interface LogEntry {
  timestamp: string
  level: LogLevel
  message: string
  data?: unknown
  component?: string
}

class Logger {
  private isDev: boolean
  private isDebugForced: boolean
  private logBuffer: LogEntry[] = []
  private maxBufferSize = 100

  constructor() {
    this.isDev = import.meta.env.DEV || import.meta.env.MODE === 'development'
    this.isDebugForced = localStorage.getItem('maestro_debug') === 'true'
  }

  private shouldLog(level: LogLevel): boolean {
    // In dev o con debug forzato, logga tutto
    if (this.isDev || this.isDebugForced) return true
    // In produzione, solo warn e error
    return level === 'warn' || level === 'error'
  }

  private formatTimestamp(): string {
    return new Date().toISOString().substr(11, 12) // HH:mm:ss.SSS
  }

  private addToBuffer(entry: LogEntry) {
    this.logBuffer.push(entry)
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer.shift()
    }
  }

  private formatMessage(level: LogLevel, message: string, component?: string): string {
    const prefix = component ? `[${component}]` : ''
    const levelEmoji = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      api: '🌐'
    }
    return `${levelEmoji[level]} ${this.formatTimestamp()} ${prefix} ${message}`
  }

  debug(message: string, data?: unknown, component?: string) {
    const entry: LogEntry = { timestamp: this.formatTimestamp(), level: 'debug', message, data, component }
    this.addToBuffer(entry)

    if (this.shouldLog('debug')) {
      console.log(this.formatMessage('debug', message, component), data !== undefined ? data : '')
    }
  }

  info(message: string, data?: unknown, component?: string) {
    const entry: LogEntry = { timestamp: this.formatTimestamp(), level: 'info', message, data, component }
    this.addToBuffer(entry)

    if (this.shouldLog('info')) {
      console.log(this.formatMessage('info', message, component), data !== undefined ? data : '')
    }
  }

  warn(message: string, data?: unknown, component?: string) {
    const entry: LogEntry = { timestamp: this.formatTimestamp(), level: 'warn', message, data, component }
    this.addToBuffer(entry)

    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, component), data !== undefined ? data : '')
    }
  }

  error(message: string, error?: unknown, component?: string) {
    const entry: LogEntry = { timestamp: this.formatTimestamp(), level: 'error', message, data: error, component }
    this.addToBuffer(entry)

    if (this.shouldLog('error')) {
      console.error(this.formatMessage('error', message, component), error !== undefined ? error : '')
    }
  }

  api(method: string, url: string, data?: unknown, component?: string) {
    const message = `${method} ${url}`
    const entry: LogEntry = { timestamp: this.formatTimestamp(), level: 'api', message, data, component }
    this.addToBuffer(entry)

    if (this.shouldLog('api')) {
      console.log(this.formatMessage('api', message, component), data !== undefined ? data : '')
    }
  }

  // Gruppo di log per operazioni multi-step
  group(name: string) {
    if (this.shouldLog('debug')) {
      console.group(`📦 ${this.formatTimestamp()} ${name}`)
    }
  }

  groupEnd() {
    if (this.shouldLog('debug')) {
      console.groupEnd()
    }
  }

  // Abilita/disabilita debug in produzione
  enableDebug() {
    localStorage.setItem('maestro_debug', 'true')
    this.isDebugForced = true
    console.log('🔧 Debug mode ATTIVATO - ricarica la pagina per vedere tutti i log')
  }

  disableDebug() {
    localStorage.removeItem('maestro_debug')
    this.isDebugForced = false
    console.log('🔧 Debug mode DISATTIVATO')
  }

  // Ottieni gli ultimi N log (utile per inviarli al supporto)
  getRecentLogs(count: number = 50): LogEntry[] {
    return this.logBuffer.slice(-count)
  }

  // Esporta log come stringa (per copia/incolla nel supporto)
  exportLogs(): string {
    return this.logBuffer
      .map(entry => {
        const dataStr = entry.data ? ` | ${JSON.stringify(entry.data)}` : ''
        return `[${entry.timestamp}] ${entry.level.toUpperCase()} ${entry.component ? `(${entry.component}) ` : ''}${entry.message}${dataStr}`
      })
      .join('\n')
  }

  // Pulisci buffer
  clearBuffer() {
    this.logBuffer = []
  }
}

// Singleton
export const logger = new Logger()

// Esponi nel window per debug da console browser
if (typeof window !== 'undefined') {
  (window as unknown as { maestroLogger: Logger }).maestroLogger = logger
}
